import React, { useState } from 'react';
import { FileText, Download, Eye, Clock, CheckCircle, AlertCircle, XCircle, MoreVertical } from 'lucide-react';

export default function DocumentLibrary({ searchQuery, filters, onDocumentClick }) {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('date');

  const documents = [
  {
    id: 1,
    title: 'Environmental Clearance Certificate',
    category: 'Environmental Permits',
    regulatoryBody: 'State Pollution Control Board',
    status: 'active',
    expiryDate: '2025-12-15',
    submissionDate: '2024-01-10',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_14cca9d2b-1765022836434.png",
    fileSize: '2.4 MB',
    fileType: 'PDF',
    version: '2.1',
    tags: ['Environment', 'Air Quality', 'Annual']
  },
  {
    id: 2,
    title: 'Factory License',
    category: 'Operational Licenses',
    regulatoryBody: 'Department of Factories',
    status: 'expiring',
    expiryDate: '2025-01-20',
    submissionDate: '2024-01-15',
    thumbnail: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&h=300&fit=crop',
    fileSize: '1.8 MB',
    fileType: 'PDF',
    version: '1.0',
    tags: ['Operations', 'License', 'Critical']
  },
  {
    id: 3,
    title: 'Fire Safety Certificate',
    category: 'Safety Certifications',
    regulatoryBody: 'Fire Department',
    status: 'active',
    expiryDate: '2026-03-10',
    submissionDate: '2024-03-10',
    thumbnail: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=400&h=300&fit=crop',
    fileSize: '3.1 MB',
    fileType: 'PDF',
    version: '1.5',
    tags: ['Safety', 'Fire', 'Annual']
  },
  {
    id: 4,
    title: 'Hazardous Waste Authorization',
    category: 'Environmental Permits',
    regulatoryBody: 'Central Pollution Control Board',
    status: 'pending',
    expiryDate: '2025-06-30',
    submissionDate: '2024-11-15',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_14d2604ca-1765022835073.png",
    fileSize: '4.2 MB',
    fileType: 'PDF',
    version: '3.0',
    tags: ['Environment', 'Waste', 'Renewal']
  },
  {
    id: 5,
    title: 'ISO 45001 Certificate',
    category: 'Quality Standards',
    regulatoryBody: 'International Organization for Standardization',
    status: 'active',
    expiryDate: '2026-09-25',
    submissionDate: '2023-09-25',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_16de233b7-1764661555596.png",
    fileSize: '2.7 MB',
    fileType: 'PDF',
    version: '2.0',
    tags: ['Quality', 'ISO', 'Safety']
  },
  {
    id: 6,
    title: 'Water Discharge Permit',
    category: 'Environmental Permits',
    regulatoryBody: 'State Water Resources Department',
    status: 'expired',
    expiryDate: '2024-11-30',
    submissionDate: '2023-11-30',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_129dcdda1-1765022835824.png",
    fileSize: '1.5 MB',
    fileType: 'PDF',
    version: '1.2',
    tags: ['Environment', 'Water', 'Renewal Required']
  }];


  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="text-green-600" size={20} />;
      case 'expiring':
        return <Clock className="text-orange-600" size={20} />;
      case 'pending':
        return <AlertCircle className="text-yellow-600" size={20} />;
      case 'expired':
        return <XCircle className="text-red-600" size={20} />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800 border-green-200',
      expiring: 'bg-orange-100 text-orange-800 border-orange-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      expired: 'bg-red-100 text-red-800 border-red-200'
    };
    return styles?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const filteredDocuments = documents?.filter((doc) => {
    const matchesSearch = doc?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    doc?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    doc?.regulatoryBody?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = filters?.category === 'all' || doc?.category === filters?.category;
    const matchesStatus = filters?.status === 'all' || doc?.status === filters?.status;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <div className="space-y-4">
      {/* View Controls */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {filteredDocuments?.length} of {documents?.length} documents
        </p>
        <div className="flex items-center gap-4">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent">

            <option value="date">Sort by Date</option>
            <option value="expiry">Sort by Expiry</option>
            <option value="category">Sort by Category</option>
            <option value="status">Sort by Status</option>
          </select>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>

              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>

              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Documents Grid/List */}
      {viewMode === 'grid' ?
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocuments?.map((doc) =>
        <div
          key={doc?.id}
          className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => onDocumentClick(doc)}>

              <div className="relative h-40 bg-gray-100">
                <img
              src={doc?.thumbnail}
              alt={`${doc?.title} document preview showing compliance certificate`}
              className="w-full h-full object-cover" />

                <div className="absolute top-2 right-2 flex gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusBadge(doc?.status)}`}>
                    {doc?.status?.charAt(0)?.toUpperCase() + doc?.status?.slice(1)}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 line-clamp-2 flex-1">
                    {doc?.title}
                  </h3>
                  {getStatusIcon(doc?.status)}
                </div>
                <p className="text-sm text-gray-600 mb-3 line-clamp-1">
                  {doc?.regulatoryBody}
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Category:</span>
                    <span className="text-gray-900 font-medium">{doc?.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Expires:</span>
                    <span className="text-gray-900 font-medium">{doc?.expiryDate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Version:</span>
                    <span className="text-gray-900 font-medium">v{doc?.version}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-3">
                  {doc?.tags?.slice(0, 3)?.map((tag, idx) =>
              <span
                key={idx}
                className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">

                      {tag}
                    </span>
              )}
                </div>
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-200">
                  <button className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    <Eye size={16} />
                    View
                  </button>
                  <button className="flex items-center justify-center p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                    <Download size={16} />
                  </button>
                  <button className="flex items-center justify-center p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                    <MoreVertical size={16} />
                  </button>
                </div>
              </div>
            </div>
        )}
        </div> :

      <div className="space-y-3">
          {filteredDocuments?.map((doc) =>
        <div
          key={doc?.id}
          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => onDocumentClick(doc)}>

              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                src={doc?.thumbnail}
                alt={`${doc?.title} thumbnail`}
                className="w-full h-full object-cover" />

                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-semibold text-gray-900 truncate flex-1">
                      {doc?.title}
                    </h3>
                    <div className="flex items-center gap-2 ml-4">
                      {getStatusIcon(doc?.status)}
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusBadge(doc?.status)}`}>
                        {doc?.status?.charAt(0)?.toUpperCase() + doc?.status?.slice(1)}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{doc?.regulatoryBody}</p>
                  <div className="flex items-center gap-6 text-sm">
                    <span className="text-gray-600">
                      <span className="font-medium">Category:</span> {doc?.category}
                    </span>
                    <span className="text-gray-600">
                      <span className="font-medium">Expires:</span> {doc?.expiryDate}
                    </span>
                    <span className="text-gray-600">
                      <span className="font-medium">Size:</span> {doc?.fileSize}
                    </span>
                    <span className="text-gray-600">
                      <span className="font-medium">Version:</span> v{doc?.version}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    <Eye size={20} />
                  </button>
                  <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                    <Download size={20} />
                  </button>
                  <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                    <MoreVertical size={20} />
                  </button>
                </div>
              </div>
            </div>
        )}
        </div>
      }

      {filteredDocuments?.length === 0 &&
      <div className="text-center py-12">
          <FileText className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
          <p className="text-gray-600">Try adjusting your search or filters</p>
        </div>
      }
    </div>);

}